import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Divider,
  Tabs,
  Tab,
  Paper,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  Settings as SettingsIcon,
  Palette as PaletteIcon,
  Image as ImageIcon,
  TextFields as TextFieldsIcon,
  ViewModule as ViewModuleIcon,
  ExpandMore as ExpandMoreIcon,
  PhoneIphone as PhoneIcon,
  Tablet as TabletIcon,
  Computer as DesktopIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SitePreview, { getSalesSiteApiBase } from './SitePreview';

const DRAWER_WIDTH = 360;

const HEADER_UI_DEFAULTS = {
  burgerBg: '#ffffff',
  burgerBorderColor: '#e5e7eb',
  burgerIconColor: '#111827',
  mobileMenuBg: '#ffffff',
  mobileMenuTextColor: '#2d3748',
  mobileMenuActiveBg: '#edf2f7',
  mobileMenuActiveTextColor: '#2d3748',
  menuTextColor: '#4a5568',
  menuHoverBg: '#f7fafc',
  menuActiveBg: '#edf2f7',
  menuFontWeight: 500,
  balanceTextColor: '#111827',
  balanceBg: '#f8fafc',
  balanceBorderColor: '#e5e7eb',
  balanceFontWeight: 600,
  plusButtonBg: '#ffffff',
  plusButtonTextColor: '#111827',
  plusButtonBorderColor: '#e5e7eb',
  plusButtonHoverBg: '#111827',
  plusButtonHoverTextColor: '#ffffff',
  avatarBg: '#f3f4f6',
  avatarTextColor: '#111827',
  avatarBorderColor: '#e5e7eb',
  avatarHoverBg: '#e5e7eb',
  dropdownBg: '#ffffff',
  dropdownTextColor: '#2d3748',
  dropdownHoverBg: '#f7fafc',
  dropdownUserBg: '#f9fafb',
  dropdownBorderColor: '#e5e7eb',
  dropdownFontWeight: 500
};

const VisualEditor = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [primaryDomain, setPrimaryDomain] = useState(null);
  const [previewKey, setPreviewKey] = useState(0);
  const [activeSection, setActiveSection] = useState('hero');
  const [viewportMode, setViewportMode] = useState('desktop'); // 'mobile', 'tablet', 'desktop'
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiApiKey, setAiApiKey] = useState('');
  const [aiModel, setAiModel] = useState('gpt-4o-mini');
  const [aiLoading, setAiLoading] = useState(false);
  const [previewSettings, setPreviewSettings] = useState(null);
  const updateTimeoutRef = useRef(null);

  const getApiBaseUrl = getSalesSiteApiBase;

  const mergeDeep = (target, source) => {
    if (!source || typeof source !== 'object' || Array.isArray(source)) {
      return target;
    }

    const output = { ...(target || {}) };
    Object.keys(source).forEach((key) => {
      const sourceValue = source[key];
      const targetValue = output[key];

      if (sourceValue && typeof sourceValue === 'object' && !Array.isArray(sourceValue)) {
        output[key] = mergeDeep(targetValue && typeof targetValue === 'object' && !Array.isArray(targetValue) ? targetValue : {}, sourceValue);
      } else {
        output[key] = sourceValue;
      }
    });

    return output;
  };

  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) {
      enqueueSnackbar('Введите промпт для генерации', { variant: 'warning' });
      return;
    }

    try {
      setAiLoading(true);
      const token = localStorage.getItem('token');
      const API_URL = getApiBaseUrl();

      const response = await axios.post(`${API_URL}sales-site/settings/ai-generate`, {
        prompt: aiPrompt,
        apiKey: aiApiKey,
        model: aiModel,
        currentSettings: settings
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data?.success && response.data?.data?.generatedSettings) {
        setSettings(prev => mergeDeep(prev, response.data.data.generatedSettings));
        enqueueSnackbar('AI конфиг применен к предпросмотру', { variant: 'success' });
      } else {
        enqueueSnackbar('AI не вернул валидные настройки', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error generating AI settings:', error);
      enqueueSnackbar(error?.response?.data?.error || 'Ошибка AI генерации', { variant: 'error' });
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
    loadPrimaryDomain();
  }, []);

  // Отправляем настройки в iframe при изменении (с debounce)
  useEffect(() => {
    if (settings) {
      // Очищаем предыдущий таймер
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }

      // Устанавливаем новый таймер
      updateTimeoutRef.current = setTimeout(() => {
        setPreviewSettings(settings);
      }, 100); // Debounce 100ms
    }

    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [settings]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const API_URL = getApiBaseUrl();
      
      const response = await axios.get(`${API_URL}sales-site/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data.data;

      // Ensure catalog object exists with defaults
      if (!data.catalog) {
        data.catalog = {
          displayMode: 'grid',
          gridColumns: 3,
          cardBg: '#ffffff',
          cardBorderColor: '#e5e7eb',
          cardHoverBorderColor: '#3b82f6',
          cardRadius: 16,
          cardScale: 100,
          cardScaleMobile: 100,
          cardWidth: 1200,
          cardWidthMobile: 1200,
          cardAlign: 'start',
          imageBlockHeight: 240,
          imageBlockHeightMobile: 220,
          cardShadow: '0 4px 16px rgba(15, 23, 42, 0.06)',
          cardHoverShadow: '0 12px 32px rgba(15, 23, 42, 0.12)',
          productNameColor: '#111827',
          productNameSize: 20,
          packagingColor: '#6b7280',
          priceColor: '#059669',
          priceSize: 24,
          badgeBg: '#eff6ff',
          badgeTextColor: '#1e40af',
          badgeBorderColor: '#bfdbfe',
          buyButtonBg: '#3b82f6',
          buyButtonTextColor: '#ffffff',
          buyButtonHoverBg: '#2563eb',
          buyButtonRadius: 12
        };
      }
      
      setSettings(data);
    } catch (error) {
      console.error('Error loading settings:', error);
      enqueueSnackbar('Ошибка загрузки настроек', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadPrimaryDomain = async () => {
    try {
      const token = localStorage.getItem('token');
      const API_URL = getApiBaseUrl();
      
      const response = await axios.get(`${API_URL}sales-site/domains`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const mainDomain = response.data.data.find(d => d.type === 'main');
      if (mainDomain) {
        setPrimaryDomain(mainDomain.domain);
      }
    } catch (error) {
      console.error('Error loading domains:', error);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const API_URL = getApiBaseUrl();
      
      await axios.put(`${API_URL}sales-site/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPreviewKey(prev => prev + 1);
      enqueueSnackbar('Изменения сохранены', { variant: 'success' });
    } catch (error) {
      console.error('Error saving settings:', error);
      enqueueSnackbar('Ошибка сохранения', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file, type) => {
    try {
      const token = localStorage.getItem('token');
      const API_URL = getApiBaseUrl();
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_URL}sales-site/settings/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`
          // Content-Type автоматически установится axios для FormData
        }
      });
      
      if (response.data.success) {
        updateSettings(type, response.data.url);
        enqueueSnackbar('Файл загружен', { variant: 'success' });
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      enqueueSnackbar('Ошибка загрузки файла', { variant: 'error' });
    }
  };

  const updateSettings = useCallback((path, value) => {
    setSettings(prev => {
      if (!prev) return prev;
      
      const keys = path.split('.');
      const newSettings = JSON.parse(JSON.stringify(prev)); // Deep clone
      let current = newSettings;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      
      return newSettings;
    });
  }, []);

  const getPickerColor = (value, fallback) => {
    const hex = typeof value === 'string' ? value.trim() : '';
    return /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(hex) ? hex : fallback;
  };

  const renderColorControl = (label, path, value, fallback, placeholder = '#ffffff') => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          type="color"
          value={getPickerColor(value, fallback)}
          onChange={(e) => updateSettings(path, e.target.value)}
          size="small"
          sx={{
            width: 64,
            minWidth: 64,
            '& .MuiInputBase-input': {
              p: 0.5,
              height: 36
            }
          }}
        />
        <TextField
          fullWidth
          label={`${label} (HTML)`}
          value={value || fallback}
          onChange={(e) => updateSettings(path, e.target.value)}
          size="small"
          placeholder={placeholder}
        />
      </Box>
    </Box>
  );

  const resetHeaderUiDefaults = () => {
    setSettings(prev => {
      if (!prev) return prev;

      return {
        ...prev,
        header: {
          ...(prev.header || {}),
          ui: { ...HEADER_UI_DEFAULTS }
        }
      };
    });

    enqueueSnackbar('Стили Header UI сброшены по умолчанию', { variant: 'info' });
  };

  if (!settings) {
    return <Box sx={{ p: 3 }}><Typography>Загрузка...</Typography></Box>;
  }

  const getViewportWidth = () => {
    switch (viewportMode) {
      case 'mobile': return '375px';
      case 'tablet': return '768px';
      case 'desktop': return '100%';
      default: return '100%';
    }
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Top Bar */}
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: 'background.paper',
          color: 'text.primary',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}
      >
        <Toolbar>
          <IconButton edge="start" onClick={() => navigate('/sales-site/design')} sx={{ mr: 2 }}>
            <CloseIcon />
          </IconButton>
          
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Визуальный редактор
          </Typography>

          <Chip 
            label={primaryDomain || 'Домен не настроен'} 
            size="small" 
            sx={{ mr: 2 }}
          />

          {/* Viewport Switcher */}
          <Paper sx={{ display: 'flex', mr: 2, p: 0.5 }}>
            <IconButton 
              size="small"
              onClick={() => setViewportMode('mobile')}
              sx={{ 
                bgcolor: viewportMode === 'mobile' ? 'primary.main' : 'transparent',
                color: viewportMode === 'mobile' ? 'white' : 'text.secondary',
                '&:hover': { bgcolor: viewportMode === 'mobile' ? 'primary.dark' : 'action.hover' }
              }}
            >
              <PhoneIcon fontSize="small" />
            </IconButton>
            <IconButton 
              size="small"
              onClick={() => setViewportMode('tablet')}
              sx={{ 
                bgcolor: viewportMode === 'tablet' ? 'primary.main' : 'transparent',
                color: viewportMode === 'tablet' ? 'white' : 'text.secondary',
                '&:hover': { bgcolor: viewportMode === 'tablet' ? 'primary.dark' : 'action.hover' }
              }}
            >
              <TabletIcon fontSize="small" />
            </IconButton>
            <IconButton 
              size="small"
              onClick={() => setViewportMode('desktop')}
              sx={{ 
                bgcolor: viewportMode === 'desktop' ? 'primary.main' : 'transparent',
                color: viewportMode === 'desktop' ? 'white' : 'text.secondary',
                '&:hover': { bgcolor: viewportMode === 'desktop' ? 'primary.dark' : 'action.hover' }
              }}
            >
              <DesktopIcon fontSize="small" />
            </IconButton>
          </Paper>

          <Button
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() => window.open(`https://${primaryDomain}`, '_blank')}
            sx={{ mr: 2 }}
            disabled={!primaryDomain}
          >
            Предпросмотр
          </Button>

          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={loading}
          >
            Сохранить
          </Button>
        </Toolbar>
      </AppBar>

      {/* Left Sidebar - Settings */}
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            mt: '64px',
            borderRight: '1px solid',
            borderColor: 'divider'
          },
        }}
      >
        <Box
          sx={{
            overflowY: 'auto',
            overflowX: 'hidden',
            height: 'calc(100vh - 64px)',
            p: 2,
            pr: 1,
            scrollbarWidth: 'thin',
            scrollbarColor: '#b0b7c3 transparent',
            '&::-webkit-scrollbar': {
              width: 8
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#b0b7c3',
              borderRadius: 8,
              border: '2px solid transparent',
              backgroundClip: 'padding-box'
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#8f99a8'
            }
          }}
        >
          {/* Header Section */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon fontSize="small" />
                <Typography fontWeight={600}>Header (Шапка)</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    Логотип
                  </Typography>
                  {settings.logo && (
                    <Box sx={{ mb: 1 }}>
                      <img 
                        src={settings.logo} 
                        alt="Logo" 
                        style={{ maxWidth: '100px', maxHeight: '50px', objectFit: 'contain' }}
                      />
                    </Box>
                  )}
                  <Button
                    variant="outlined"
                    component="label"
                    size="small"
                    startIcon={<UploadIcon />}
                    fullWidth
                  >
                    Загрузить логотип
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          handleFileUpload(e.target.files[0], 'logo');
                        }
                      }}
                    />
                  </Button>
                </Box>

                <TextField
                  fullWidth
                  label="Название сайта"
                  value={settings.siteName || ''}
                  onChange={(e) => updateSettings('siteName', e.target.value)}
                  size="small"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.header?.showLogo ?? true}
                      onChange={(e) => updateSettings('header.showLogo', e.target.checked)}
                    />
                  }
                  label="Показывать логотип"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.header?.showSiteName ?? true}
                      onChange={(e) => updateSettings('header.showSiteName', e.target.checked)}
                    />
                  }
                  label="Показывать название"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.header?.showMenu ?? true}
                      onChange={(e) => updateSettings('header.showMenu', e.target.checked)}
                    />
                  }
                  label="Показывать меню"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.header?.sticky ?? true}
                      onChange={(e) => updateSettings('header.sticky', e.target.checked)}
                    />
                  }
                  label="Прилипающий header"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.header?.shadow ?? true}
                      onChange={(e) => updateSettings('header.shadow', e.target.checked)}
                    />
                  }
                  label="Тень"
                />

                <Divider />

                <Typography variant="subtitle2" fontWeight={600}>Цвета</Typography>

                <FormControl fullWidth size="small">
                  <InputLabel>Тип фона хедера</InputLabel>
                  <Select
                    value={typeof settings.header?.backgroundColor === 'object' ? settings.header.backgroundColor.type : 'color'}
                    onChange={(e) => {
                      if (e.target.value === 'color') {
                        updateSettings('header.backgroundColor', '#ffffff');
                      } else {
                        updateSettings('header.backgroundColor', {
                          type: 'gradient',
                          gradient: { from: '#ffffff', to: '#f3f4f6' }
                        });
                      }
                    }}
                    label="Тип фона хедера"
                  >
                    <MenuItem value="color">Цвет</MenuItem>
                    <MenuItem value="gradient">Градиент</MenuItem>
                  </Select>
                </FormControl>

                {typeof settings.header?.backgroundColor === 'object' && settings.header.backgroundColor.type === 'gradient' ? (
                  <>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        label="Цвет 1"
                        type="color"
                        value={getPickerColor(settings.header.backgroundColor.gradient?.from, '#ffffff')}
                        onChange={(e) => updateSettings('header.backgroundColor.gradient.from', e.target.value)}
                        size="small"
                        sx={{ width: 90 }}
                      />
                      <TextField
                        fullWidth
                        label="HTML цвет 1"
                        value={settings.header.backgroundColor.gradient?.from || '#ffffff'}
                        onChange={(e) => updateSettings('header.backgroundColor.gradient.from', e.target.value)}
                        size="small"
                        placeholder="#ffffff"
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        label="Цвет 2"
                        type="color"
                        value={getPickerColor(settings.header.backgroundColor.gradient?.to, '#f3f4f6')}
                        onChange={(e) => updateSettings('header.backgroundColor.gradient.to', e.target.value)}
                        size="small"
                        sx={{ width: 90 }}
                      />
                      <TextField
                        fullWidth
                        label="HTML цвет 2"
                        value={settings.header.backgroundColor.gradient?.to || '#f3f4f6'}
                        onChange={(e) => updateSettings('header.backgroundColor.gradient.to', e.target.value)}
                        size="small"
                        placeholder="#f3f4f6"
                      />
                    </Box>
                  </>
                ) : (
                  renderColorControl('Фон', 'header.backgroundColor', settings.header?.backgroundColor, '#ffffff', '#ffffff или white')
                )}
                {renderColorControl('Текст', 'header.textColor', settings.header?.textColor, '#2d3748', '#2d3748 или black')}

                <Divider />

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>UI детали header</Typography>
                  <Button size="small" variant="outlined" onClick={resetHeaderUiDefaults}>
                    Сбросить по умолчанию
                  </Button>
                </Box>

                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={600}>Бургер и мобильное меню</Typography>
                  {renderColorControl('Бургер фон', 'header.ui.burgerBg', settings.header?.ui?.burgerBg, '#ffffff')}
                  {renderColorControl('Бургер обводка', 'header.ui.burgerBorderColor', settings.header?.ui?.burgerBorderColor, '#e5e7eb')}
                  {renderColorControl('Бургер линии', 'header.ui.burgerIconColor', settings.header?.ui?.burgerIconColor, '#111827')}
                  {renderColorControl('Моб. меню фон', 'header.ui.mobileMenuBg', settings.header?.ui?.mobileMenuBg, '#ffffff')}
                  {renderColorControl('Моб. меню текст', 'header.ui.mobileMenuTextColor', settings.header?.ui?.mobileMenuTextColor, '#2d3748')}
                  {renderColorControl('Моб. активный фон', 'header.ui.mobileMenuActiveBg', settings.header?.ui?.mobileMenuActiveBg, '#edf2f7')}
                  {renderColorControl('Моб. активный текст', 'header.ui.mobileMenuActiveTextColor', settings.header?.ui?.mobileMenuActiveTextColor, '#2d3748')}
                </Box>

                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={600}>Меню</Typography>
                  {renderColorControl('Меню текст', 'header.ui.menuTextColor', settings.header?.ui?.menuTextColor, '#4a5568')}
                  {renderColorControl('Меню hover фон', 'header.ui.menuHoverBg', settings.header?.ui?.menuHoverBg, '#f7fafc')}
                  {renderColorControl('Меню active фон', 'header.ui.menuActiveBg', settings.header?.ui?.menuActiveBg, '#edf2f7')}
                  <FormControl fullWidth size="small">
                    <InputLabel>Жирность меню</InputLabel>
                    <Select
                      value={settings.header?.ui?.menuFontWeight || 500}
                      onChange={(e) => updateSettings('header.ui.menuFontWeight', Number(e.target.value))}
                      label="Жирность меню"
                    >
                      <MenuItem value={400}>400</MenuItem>
                      <MenuItem value={500}>500</MenuItem>
                      <MenuItem value={600}>600</MenuItem>
                      <MenuItem value={700}>700</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={600}>Баланс и кнопка +</Typography>
                  {renderColorControl('Баланс текст', 'header.ui.balanceTextColor', settings.header?.ui?.balanceTextColor, '#111827')}
                  {renderColorControl('Баланс фон', 'header.ui.balanceBg', settings.header?.ui?.balanceBg, '#f8fafc')}
                  {renderColorControl('Баланс бордер', 'header.ui.balanceBorderColor', settings.header?.ui?.balanceBorderColor, '#e5e7eb')}
                  <FormControl fullWidth size="small">
                    <InputLabel>Жирность баланса</InputLabel>
                    <Select
                      value={settings.header?.ui?.balanceFontWeight || 600}
                      onChange={(e) => updateSettings('header.ui.balanceFontWeight', Number(e.target.value))}
                      label="Жирность баланса"
                    >
                      <MenuItem value={400}>400</MenuItem>
                      <MenuItem value={500}>500</MenuItem>
                      <MenuItem value={600}>600</MenuItem>
                      <MenuItem value={700}>700</MenuItem>
                    </Select>
                  </FormControl>
                  {renderColorControl('Кнопка + фон', 'header.ui.plusButtonBg', settings.header?.ui?.plusButtonBg, '#ffffff')}
                  {renderColorControl('Кнопка + текст', 'header.ui.plusButtonTextColor', settings.header?.ui?.plusButtonTextColor, '#111827')}
                  {renderColorControl('Кнопка + бордер', 'header.ui.plusButtonBorderColor', settings.header?.ui?.plusButtonBorderColor, '#e5e7eb')}
                  {renderColorControl('Кнопка + hover фон', 'header.ui.plusButtonHoverBg', settings.header?.ui?.plusButtonHoverBg, '#111827')}
                  {renderColorControl('Кнопка + hover текст', 'header.ui.plusButtonHoverTextColor', settings.header?.ui?.plusButtonHoverTextColor, '#ffffff')}
                </Box>

                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={600}>Аватар</Typography>
                  {renderColorControl('Аватар фон', 'header.ui.avatarBg', settings.header?.ui?.avatarBg, '#f3f4f6')}
                  {renderColorControl('Аватар текст', 'header.ui.avatarTextColor', settings.header?.ui?.avatarTextColor, '#111827')}
                  {renderColorControl('Аватар бордер', 'header.ui.avatarBorderColor', settings.header?.ui?.avatarBorderColor, '#e5e7eb')}
                  {renderColorControl('Аватар hover фон', 'header.ui.avatarHoverBg', settings.header?.ui?.avatarHoverBg, '#e5e7eb')}
                </Box>

                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={600}>Dropdown меню</Typography>
                  {renderColorControl('Dropdown фон', 'header.ui.dropdownBg', settings.header?.ui?.dropdownBg, '#ffffff')}
                  {renderColorControl('Dropdown текст', 'header.ui.dropdownTextColor', settings.header?.ui?.dropdownTextColor, '#2d3748')}
                  {renderColorControl('Dropdown hover', 'header.ui.dropdownHoverBg', settings.header?.ui?.dropdownHoverBg, '#f7fafc')}
                  {renderColorControl('Dropdown user фон', 'header.ui.dropdownUserBg', settings.header?.ui?.dropdownUserBg, '#f9fafb')}
                  {renderColorControl('Dropdown бордер', 'header.ui.dropdownBorderColor', settings.header?.ui?.dropdownBorderColor, '#e5e7eb')}
                  <FormControl fullWidth size="small">
                    <InputLabel>Жирность dropdown</InputLabel>
                    <Select
                      value={settings.header?.ui?.dropdownFontWeight || 500}
                      onChange={(e) => updateSettings('header.ui.dropdownFontWeight', Number(e.target.value))}
                      label="Жирность dropdown"
                    >
                      <MenuItem value={400}>400</MenuItem>
                      <MenuItem value={500}>500</MenuItem>
                      <MenuItem value={600}>600</MenuItem>
                      <MenuItem value={700}>700</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* AI Generator Section */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon fontSize="small" />
                <Typography fontWeight={600}>AI Генератор</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="OpenAI API Key"
                  type="password"
                  value={aiApiKey}
                  onChange={(e) => setAiApiKey(e.target.value)}
                  size="small"
                  placeholder="sk-... (или оставьте пустым для .env)"
                />
                <TextField
                  fullWidth
                  label="Модель"
                  value={aiModel}
                  onChange={(e) => setAiModel(e.target.value)}
                  size="small"
                  placeholder="gpt-4o-mini"
                />
                <TextField
                  fullWidth
                  label="Промпт для генерации"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  size="small"
                  multiline
                  rows={5}
                  placeholder="Сделай современный минималистичный дизайн в синих тонах, крупный hero, компактные карточки на мобиле..."
                />
                <Button
                  variant="contained"
                  onClick={handleAiGenerate}
                  disabled={aiLoading}
                >
                  {aiLoading ? 'Генерация...' : 'Сгенерировать конфиг'}
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Hero Section */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ImageIcon fontSize="small" />
                <Typography fontWeight={600}>Hero секция</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.hero.enabled}
                      onChange={(e) => updateSettings('hero.enabled', e.target.checked)}
                    />
                  }
                  label="Показывать"
                />

                {settings.hero.enabled && (
                  <>
                    <TextField
                      fullWidth
                      label="Заголовок"
                      value={settings.hero.title}
                      onChange={(e) => updateSettings('hero.title', e.target.value)}
                      size="small"
                    />

                    <TextField
                      fullWidth
                      label="Подзаголовок"
                      value={settings.hero.subtitle}
                      onChange={(e) => updateSettings('hero.subtitle', e.target.value)}
                      multiline
                      rows={2}
                      size="small"
                    />

                    <FormControl fullWidth size="small">
                      <InputLabel>Высота</InputLabel>
                      <Select
                        value={settings.hero.height}
                        onChange={(e) => updateSettings('hero.height', e.target.value)}
                        label="Высота"
                      >
                        <MenuItem value="small">Маленькая</MenuItem>
                        <MenuItem value="medium">Средняя</MenuItem>
                        <MenuItem value="large">Большая</MenuItem>
                      </Select>
                    </FormControl>

                    <Divider />

                    <Typography variant="subtitle2" fontWeight={600}>Фон</Typography>

                    <FormControl fullWidth size="small">
                      <InputLabel>Тип фона</InputLabel>
                      <Select
                        value={settings.hero.background?.type || 'gradient'}
                        onChange={(e) => updateSettings('hero.background.type', e.target.value)}
                        label="Тип фона"
                      >
                        <MenuItem value="color">Цвет</MenuItem>
                        <MenuItem value="gradient">Градиент</MenuItem>
                        <MenuItem value="image">Изображение</MenuItem>
                      </Select>
                    </FormControl>

                    {settings.hero.background?.type === 'color' && (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          label="Цвет"
                          type="color"
                          value={getPickerColor(settings.hero.background?.color, '#3B82F6')}
                          onChange={(e) => updateSettings('hero.background.color', e.target.value)}
                          size="small"
                          sx={{ width: 90 }}
                        />
                        <TextField
                          fullWidth
                          label="HTML цвет"
                          value={settings.hero.background?.color || '#3B82F6'}
                          onChange={(e) => updateSettings('hero.background.color', e.target.value)}
                          size="small"
                          placeholder="#3B82F6"
                        />
                      </Box>
                    )}

                    {settings.hero.background.type === 'gradient' && (
                      <>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField
                            label="Цвет 1"
                            type="color"
                            value={getPickerColor(settings.hero.background.gradient.from, '#000000')}
                            onChange={(e) => updateSettings('hero.background.gradient.from', e.target.value)}
                            size="small"
                            sx={{ width: 90 }}
                          />
                          <TextField
                            fullWidth
                            label="HTML цвет 1"
                            value={settings.hero.background.gradient.from}
                            onChange={(e) => updateSettings('hero.background.gradient.from', e.target.value)}
                            size="small"
                            placeholder="#667eea"
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField
                            label="Цвет 2"
                            type="color"
                            value={getPickerColor(settings.hero.background.gradient.to, '#000000')}
                            onChange={(e) => updateSettings('hero.background.gradient.to', e.target.value)}
                            size="small"
                            sx={{ width: 90 }}
                          />
                          <TextField
                            fullWidth
                            label="HTML цвет 2"
                            value={settings.hero.background.gradient.to}
                            onChange={(e) => updateSettings('hero.background.gradient.to', e.target.value)}
                            size="small"
                            placeholder="#764ba2"
                          />
                        </Box>
                      </>
                    )}

                    {settings.hero.background?.type === 'image' && (
                      <>
                        <TextField
                          fullWidth
                          label="URL изображения"
                          value={settings.hero.background?.image || ''}
                          onChange={(e) => updateSettings('hero.background.image', e.target.value)}
                          size="small"
                          placeholder="https://... или /api/uploads/..."
                        />
                        <Button
                          variant="outlined"
                          component="label"
                          size="small"
                          startIcon={<UploadIcon />}
                          fullWidth
                        >
                          Загрузить фон Hero
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={(e) => {
                              if (e.target.files[0]) {
                                handleFileUpload(e.target.files[0], 'hero.background.image');
                              }
                            }}
                          />
                        </Button>
                      </>
                    )}
                  </>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* General Settings Section */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon fontSize="small" />
                <Typography fontWeight={600}>Общее</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Название сайта"
                  value={settings.siteName || ''}
                  onChange={(e) => updateSettings('siteName', e.target.value)}
                  size="small"
                  placeholder="Мой магазин"
                />

                <Divider />

                <Typography variant="subtitle2" fontWeight={600}>Фон страницы</Typography>
                
                <FormControl fullWidth size="small">
                  <InputLabel>Тип фона</InputLabel>
                  <Select
                    value={typeof settings.colors?.background === 'object' ? settings.colors.background.type : 'color'}
                    onChange={(e) => {
                      if (e.target.value === 'color') {
                        updateSettings('colors.background', '#ffffff');
                      } else {
                        updateSettings('colors.background', {
                          type: 'gradient',
                          gradient: { from: '#ffffff', to: '#f3f4f6' }
                        });
                      }
                    }}
                    label="Тип фона"
                  >
                    <MenuItem value="color">Цвет</MenuItem>
                    <MenuItem value="gradient">Градиент</MenuItem>
                  </Select>
                </FormControl>

                {typeof settings.colors?.background === 'object' && settings.colors.background.type === 'gradient' ? (
                  <>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        label="Цвет 1"
                        type="color"
                        value={getPickerColor(settings.colors.background.gradient?.from, '#ffffff')}
                        onChange={(e) => updateSettings('colors.background.gradient.from', e.target.value)}
                        size="small"
                        sx={{ width: 90 }}
                      />
                      <TextField
                        fullWidth
                        label="HTML цвет 1"
                        value={settings.colors.background.gradient?.from || '#ffffff'}
                        onChange={(e) => updateSettings('colors.background.gradient.from', e.target.value)}
                        size="small"
                        placeholder="#ffffff"
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        label="Цвет 2"
                        type="color"
                        value={getPickerColor(settings.colors.background.gradient?.to, '#f3f4f6')}
                        onChange={(e) => updateSettings('colors.background.gradient.to', e.target.value)}
                        size="small"
                        sx={{ width: 90 }}
                      />
                      <TextField
                        fullWidth
                        label="HTML цвет 2"
                        value={settings.colors.background.gradient?.to || '#f3f4f6'}
                        onChange={(e) => updateSettings('colors.background.gradient.to', e.target.value)}
                        size="small"
                        placeholder="#f3f4f6"
                      />
                    </Box>
                  </>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      label="Цвет фона"
                      type="color"
                      value={getPickerColor(settings.colors?.background, '#ffffff')}
                      onChange={(e) => updateSettings('colors.background', e.target.value)}
                      size="small"
                      sx={{ width: 90 }}
                    />
                    <TextField
                      fullWidth
                      label="HTML цвет"
                      value={typeof settings.colors?.background === 'string' ? settings.colors.background : '#ffffff'}
                      onChange={(e) => updateSettings('colors.background', e.target.value)}
                      size="small"
                      placeholder="#ffffff"
                    />
                  </Box>
                )}

                <Divider />

                <Typography variant="subtitle2" fontWeight={600}>SEO</Typography>
                <TextField
                  fullWidth
                  label="Title (заголовок страницы)"
                  value={settings.seo?.title || ''}
                  onChange={(e) => updateSettings('seo.title', e.target.value)}
                  size="small"
                  placeholder="Название сайта"
                />
                <TextField
                  fullWidth
                  label="Description (описание)"
                  value={settings.seo?.description || ''}
                  onChange={(e) => updateSettings('seo.description', e.target.value)}
                  size="small"
                  multiline
                  rows={2}
                  placeholder="Описание вашего сайта"
                />
                <TextField
                  fullWidth
                  label="Keywords (ключевые слова)"
                  value={settings.seo?.keywords || ''}
                  onChange={(e) => updateSettings('seo.keywords', e.target.value)}
                  size="small"
                  placeholder="ключ1, ключ2, ключ3"
                />

                <Divider />

                <Typography variant="subtitle2" fontWeight={600}>Favicon</Typography>
                {settings.favicon && (
                  <Box sx={{ mb: 1 }}>
                    <img 
                      src={settings.favicon} 
                      alt="Favicon" 
                      style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                    />
                  </Box>
                )}
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  startIcon={<UploadIcon />}
                  fullWidth
                >
                  Загрузить favicon
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        handleFileUpload(e.target.files[0], 'favicon');
                      }
                    }}
                  />
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Typography Section */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextFieldsIcon fontSize="small" />
                <Typography fontWeight={600}>Типографика</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Шрифт заголовков</InputLabel>
                  <Select
                    value={settings.fonts.heading}
                    onChange={(e) => updateSettings('fonts.heading', e.target.value)}
                    label="Шрифт заголовков"
                  >
                    <MenuItem value="Roboto">Roboto</MenuItem>
                    <MenuItem value="Montserrat">Montserrat</MenuItem>
                    <MenuItem value="Open Sans">Open Sans</MenuItem>
                    <MenuItem value="Lato">Lato</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>Шрифт текста</InputLabel>
                  <Select
                    value={settings.fonts.body}
                    onChange={(e) => updateSettings('fonts.body', e.target.value)}
                    label="Шрифт текста"
                  >
                    <MenuItem value="Roboto">Roboto</MenuItem>
                    <MenuItem value="Montserrat">Montserrat</MenuItem>
                    <MenuItem value="Open Sans">Open Sans</MenuItem>
                    <MenuItem value="Lato">Lato</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Product Cards Section */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ViewModuleIcon fontSize="small" />
                <Typography fontWeight={600}>Фильтры каталога</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.filters?.enabled ?? true}
                      onChange={(e) => updateSettings('filters.enabled', e.target.checked)}
                    />
                  }
                  label="Показывать блок фильтров"
                />

                <TextField
                  fullWidth
                  label="Заголовок"
                  value={settings.filters?.title || ''}
                  onChange={(e) => updateSettings('filters.title', e.target.value)}
                  size="small"
                />

                <TextField
                  fullWidth
                  label="Подзаголовок"
                  value={settings.filters?.subtitle || ''}
                  onChange={(e) => updateSettings('filters.subtitle', e.target.value)}
                  size="small"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.filters?.showResultCount ?? true}
                      onChange={(e) => updateSettings('filters.showResultCount', e.target.checked)}
                    />
                  }
                  label="Показывать счетчик найденных"
                />

                <Divider />
                <Typography variant="subtitle2" fontWeight={600}>Цвета блока</Typography>
                {renderColorControl('Фон блока', 'filters.ui.bg', settings.filters?.ui?.bg, '#ffffff')}
                {renderColorControl('Бордер блока', 'filters.ui.borderColor', settings.filters?.ui?.borderColor, '#e5e7eb')}
                {renderColorControl('Текст заголовка', 'filters.ui.textColor', settings.filters?.ui?.textColor, '#111827')}
                {renderColorControl('Текст подписей', 'filters.ui.labelColor', settings.filters?.ui?.labelColor, '#6b7280')}

                <Divider />
                <Typography variant="subtitle2" fontWeight={600}>Поля фильтров</Typography>
                {renderColorControl('Фон поля', 'filters.ui.inputBg', settings.filters?.ui?.inputBg, '#ffffff')}
                {renderColorControl('Текст поля', 'filters.ui.inputTextColor', settings.filters?.ui?.inputTextColor, '#111827')}
                {renderColorControl('Бордер поля', 'filters.ui.inputBorderColor', settings.filters?.ui?.inputBorderColor, '#d1d5db')}

                <Divider />
                <Typography variant="subtitle2" fontWeight={600}>Карточки выдачи</Typography>
                {renderColorControl('Фон карточки', 'filters.ui.cardBg', settings.filters?.ui?.cardBg, '#f9fafb')}
                {renderColorControl('Бордер карточки', 'filters.ui.cardBorderColor', settings.filters?.ui?.cardBorderColor, '#e5e7eb')}
                {renderColorControl('Заголовок карточки', 'filters.ui.cardTitleColor', settings.filters?.ui?.cardTitleColor, '#111827')}
                {renderColorControl('Мета карточки', 'filters.ui.cardMetaColor', settings.filters?.ui?.cardMetaColor, '#4b5563')}
                {renderColorControl('Цвет счетчика', 'filters.ui.countColor', settings.filters?.ui?.countColor, '#374151')}

                <Divider />
                <Typography variant="subtitle2" fontWeight={600}>Акценты и chips</Typography>
                {renderColorControl('Основной акцент', 'filters.ui.accentColor', settings.filters?.ui?.accentColor, '#3b82f6')}
                {renderColorControl('Текст акцента', 'filters.ui.accentTextColor', settings.filters?.ui?.accentTextColor, '#1e3a8a')}
                {renderColorControl('Chip active фон', 'filters.ui.chipActiveBg', settings.filters?.ui?.chipActiveBg, '#3b82f6')}
                {renderColorControl('Chip active бордер', 'filters.ui.chipActiveBorderColor', settings.filters?.ui?.chipActiveBorderColor, '#3b82f6')}
                {renderColorControl('Chip active текст', 'filters.ui.chipActiveTextColor', settings.filters?.ui?.chipActiveTextColor, '#1e3a8a')}
                {renderColorControl('Chip hover бордер', 'filters.ui.chipHoverBorderColor', settings.filters?.ui?.chipHoverBorderColor, '#9ca3af')}

                <Divider />
                <Typography variant="subtitle2" fontWeight={600}>Кнопка сброса</Typography>
                {renderColorControl('Reset фон', 'filters.ui.resetButtonBg', settings.filters?.ui?.resetButtonBg, '#ffffff')}
                {renderColorControl('Reset текст', 'filters.ui.resetButtonTextColor', settings.filters?.ui?.resetButtonTextColor, '#111827')}
                {renderColorControl('Reset бордер', 'filters.ui.resetButtonBorderColor', settings.filters?.ui?.resetButtonBorderColor, '#d1d5db')}
                {renderColorControl('Reset hover фон', 'filters.ui.resetButtonHoverBg', settings.filters?.ui?.resetButtonHoverBg, '#eff6ff')}
                {renderColorControl('Reset hover текст', 'filters.ui.resetButtonHoverTextColor', settings.filters?.ui?.resetButtonHoverTextColor, '#1e3a8a')}
                {renderColorControl('Reset hover бордер', 'filters.ui.resetButtonHoverBorderColor', settings.filters?.ui?.resetButtonHoverBorderColor, '#3b82f6')}

                <Divider />
                <Typography variant="subtitle2" fontWeight={600}>Геометрия и тени</Typography>
                <TextField
                  fullWidth
                  label="Колонок фильтров"
                  type="number"
                  value={settings.filters?.ui?.filterColumns ?? 5}
                  onChange={(e) => updateSettings('filters.ui.filterColumns', Number(e.target.value) || 1)}
                  size="small"
                  inputProps={{ min: 1, max: 6 }}
                />
                <TextField
                  fullWidth
                  label="Размер заголовка"
                  type="number"
                  value={settings.filters?.ui?.titleSize ?? 26}
                  onChange={(e) => updateSettings('filters.ui.titleSize', Number(e.target.value) || 26)}
                  size="small"
                  inputProps={{ min: 16, max: 48 }}
                />
                <TextField
                  fullWidth
                  label="Радиус панели"
                  type="number"
                  value={settings.filters?.ui?.panelRadius ?? 18}
                  onChange={(e) => updateSettings('filters.ui.panelRadius', Number(e.target.value) || 18)}
                  size="small"
                  inputProps={{ min: 0, max: 40 }}
                />
                <TextField
                  fullWidth
                  label="Радиус карточек"
                  type="number"
                  value={settings.filters?.ui?.cardRadius ?? 14}
                  onChange={(e) => updateSettings('filters.ui.cardRadius', Number(e.target.value) || 14)}
                  size="small"
                  inputProps={{ min: 0, max: 40 }}
                />
                <TextField
                  fullWidth
                  label="Радиус chips"
                  type="number"
                  value={settings.filters?.ui?.chipRadius ?? 999}
                  onChange={(e) => updateSettings('filters.ui.chipRadius', Number(e.target.value) || 999)}
                  size="small"
                  inputProps={{ min: 0, max: 999 }}
                />

                <TextField
                  fullWidth
                  label="Тень панели"
                  value={settings.filters?.ui?.panelShadow || '0 10px 35px rgba(15, 23, 42, 0.08)'}
                  onChange={(e) => updateSettings('filters.ui.panelShadow', e.target.value)}
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Тень карточки"
                  value={settings.filters?.ui?.cardShadow || '0 6px 20px rgba(15, 23, 42, 0.05)'}
                  onChange={(e) => updateSettings('filters.ui.cardShadow', e.target.value)}
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Тень карточки hover"
                  value={settings.filters?.ui?.cardHoverShadow || '0 10px 30px rgba(15, 23, 42, 0.11)'}
                  onChange={(e) => updateSettings('filters.ui.cardHoverShadow', e.target.value)}
                  size="small"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Catalog Display Section */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ViewModuleIcon fontSize="small" />
                <Typography fontWeight={600}>Каталог - отображение</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                
                {/* Display Mode */}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Режим отображения
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={settings.catalog?.displayMode || 'grid'}
                      onChange={(e) => updateSettings('catalog.displayMode', e.target.value)}
                    >
                      <MenuItem value="grid">Сетка</MenuItem>
                      <MenuItem value="list">Список</MenuItem>
                      <MenuItem value="compact">Компактная сетка</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Grid Columns (only for grid/compact modes) */}
                {(settings.catalog?.displayMode === 'grid' || settings.catalog?.displayMode === 'compact' || !settings.catalog?.displayMode) && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Колонок в сетке: {settings.catalog?.gridColumns || 3}
                    </Typography>
                    <Slider
                      value={Number(settings.catalog?.gridColumns) || 3}
                      onChange={(e, val) => updateSettings('catalog.gridColumns', Number(val))}
                      min={2}
                      max={5}
                      step={1}
                      marks
                      size="small"
                      valueLabelDisplay="auto"
                    />
                  </Box>
                )}

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Выравнивание карточек
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={settings.catalog?.cardAlign || 'start'}
                      onChange={(e) => updateSettings('catalog.cardAlign', e.target.value)}
                    >
                      <MenuItem value="start">Слева</MenuItem>
                      <MenuItem value="center">По центру</MenuItem>
                      <MenuItem value="end">Справа</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Divider />

                {/* Card Colors */}
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  Цвета карточек
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      Фон карточки
                    </Typography>
                    <TextField
                      type="color"
                      value={settings.catalog?.cardBg || '#ffffff'}
                      onChange={(e) => updateSettings('catalog.cardBg', e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      Рамка
                    </Typography>
                    <TextField
                      type="color"
                      value={settings.catalog?.cardBorderColor || '#e5e7eb'}
                      onChange={(e) => updateSettings('catalog.cardBorderColor', e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      Рамка (hover)
                    </Typography>
                    <TextField
                      type="color"
                      value={settings.catalog?.cardHoverBorderColor || '#3b82f6'}
                      onChange={(e) => updateSettings('catalog.cardHoverBorderColor', e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      Название товара
                    </Typography>
                    <TextField
                      type="color"
                      value={settings.catalog?.productNameColor || '#111827'}
                      onChange={(e) => updateSettings('catalog.productNameColor', e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      Фасовка
                    </Typography>
                    <TextField
                      type="color"
                      value={settings.catalog?.packagingColor || '#6b7280'}
                      onChange={(e) => updateSettings('catalog.packagingColor', e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      Цена
                    </Typography>
                    <TextField
                      type="color"
                      value={settings.catalog?.priceColor || '#059669'}
                      onChange={(e) => updateSettings('catalog.priceColor', e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      Фон бейджа
                    </Typography>
                    <TextField
                      type="color"
                      value={settings.catalog?.badgeBg || '#eff6ff'}
                      onChange={(e) => updateSettings('catalog.badgeBg', e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      Текст бейджа
                    </Typography>
                    <TextField
                      type="color"
                      value={settings.catalog?.badgeTextColor || '#1e40af'}
                      onChange={(e) => updateSettings('catalog.badgeTextColor', e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      Рамка бейджа
                    </Typography>
                    <TextField
                      type="color"
                      value={settings.catalog?.badgeBorderColor || '#bfdbfe'}
                      onChange={(e) => updateSettings('catalog.badgeBorderColor', e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Box>
                </Box>

                <Divider />

                {/* Typography */}
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  Размеры текста
                </Typography>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Название товара: {settings.catalog?.productNameSize || 20}px
                  </Typography>
                  <Slider
                    value={Number(settings.catalog?.productNameSize) || 20}
                    onChange={(e, val) => updateSettings('catalog.productNameSize', Number(val))}
                    min={14}
                    max={32}
                    step={1}
                    size="small"
                    valueLabelDisplay="auto"
                  />
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Ширина блока карточек (ПК): {settings.catalog?.cardWidth || 1200}px
                  </Typography>
                  <Slider
                    value={Number(settings.catalog?.cardWidth) || 1200}
                    onChange={(e, val) => updateSettings('catalog.cardWidth', Number(val))}
                    min={800}
                    max={1800}
                    step={20}
                    size="small"
                    valueLabelDisplay="auto"
                  />
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Ширина блока карточек (моб.): {settings.catalog?.cardWidthMobile || 1200}px
                  </Typography>
                  <Slider
                    value={Number(settings.catalog?.cardWidthMobile) || 1200}
                    onChange={(e, val) => updateSettings('catalog.cardWidthMobile', Number(val))}
                    min={280}
                    max={800}
                    step={10}
                    size="small"
                    valueLabelDisplay="auto"
                  />
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Цена: {settings.catalog?.priceSize || 24}px
                  </Typography>
                  <Slider
                    value={Number(settings.catalog?.priceSize) || 24}
                    onChange={(e, val) => updateSettings('catalog.priceSize', Number(val))}
                    min={16}
                    max={36}
                    step={1}
                    size="small"
                    valueLabelDisplay="auto"
                  />
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Высота блока фото (ПК): {settings.catalog?.imageBlockHeight || 240}px
                  </Typography>
                  <Slider
                    value={Number(settings.catalog?.imageBlockHeight) || 240}
                    onChange={(e, val) => updateSettings('catalog.imageBlockHeight', Number(val))}
                    min={140}
                    max={420}
                    step={10}
                    size="small"
                    valueLabelDisplay="auto"
                  />
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Высота блока фото (моб.): {settings.catalog?.imageBlockHeightMobile || 220}px
                  </Typography>
                  <Slider
                    value={Number(settings.catalog?.imageBlockHeightMobile) || 220}
                    onChange={(e, val) => updateSettings('catalog.imageBlockHeightMobile', Number(val))}
                    min={120}
                    max={360}
                    step={10}
                    size="small"
                    valueLabelDisplay="auto"
                  />
                </Box>

                <Divider />

                {/* Geometry */}
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  Геометрия
                </Typography>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Скругление карточки: {settings.catalog?.cardRadius || 16}px
                  </Typography>
                  <Slider
                    value={Number(settings.catalog?.cardRadius) || 16}
                    onChange={(e, val) => updateSettings('catalog.cardRadius', Number(val))}
                    min={0}
                    max={32}
                    step={2}
                    size="small"
                    valueLabelDisplay="auto"
                  />
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Размер карточки: {settings.catalog?.cardScale || 100}%
                  </Typography>
                  <Slider
                    value={Number(settings.catalog?.cardScale) || 100}
                    onChange={(e, val) => updateSettings('catalog.cardScale', Number(val))}
                    min={80}
                    max={120}
                    step={2}
                    size="small"
                    valueLabelDisplay="auto"
                  />
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Размер карточки (моб.): {settings.catalog?.cardScaleMobile || 100}%
                  </Typography>
                  <Slider
                    value={Number(settings.catalog?.cardScaleMobile) || 100}
                    onChange={(e, val) => updateSettings('catalog.cardScaleMobile', Number(val))}
                    min={70}
                    max={120}
                    step={2}
                    size="small"
                    valueLabelDisplay="auto"
                  />
                </Box>

                <Divider />

                {/* Shadows */}
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  Тени
                </Typography>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    Тень карточки
                  </Typography>
                  <TextField
                    value={settings.catalog?.cardShadow || '0 4px 16px rgba(15, 23, 42, 0.06)'}
                    onChange={(e) => updateSettings('catalog.cardShadow', e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="0 4px 16px rgba(15, 23, 42, 0.06)"
                  />
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    Тень при наведении
                  </Typography>
                  <TextField
                    value={settings.catalog?.cardHoverShadow || '0 12px 32px rgba(15, 23, 42, 0.12)'}
                    onChange={(e) => updateSettings('catalog.cardHoverShadow', e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="0 12px 32px rgba(15, 23, 42, 0.12)"
                  />
                </Box>

                <Divider />

                {/* Buy Button */}
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  Кнопка "Купить"
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      Фон кнопки
                    </Typography>
                    <TextField
                      type="color"
                      value={settings.catalog?.buyButtonBg || '#3b82f6'}
                      onChange={(e) => updateSettings('catalog.buyButtonBg', e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      Текст кнопки
                    </Typography>
                    <TextField
                      type="color"
                      value={settings.catalog?.buyButtonTextColor || '#ffffff'}
                      onChange={(e) => updateSettings('catalog.buyButtonTextColor', e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      Фон (hover)
                    </Typography>
                    <TextField
                      type="color"
                      value={settings.catalog?.buyButtonHoverBg || '#2563eb'}
                      onChange={(e) => updateSettings('catalog.buyButtonHoverBg', e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Box>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Скругление кнопки: {settings.catalog?.buyButtonRadius || 12}px
                  </Typography>
                  <Slider
                    value={Number(settings.catalog?.buyButtonRadius) || 12}
                    onChange={(e, val) => updateSettings('catalog.buyButtonRadius', Number(val))}
                    min={0}
                    max={24}
                    step={2}
                    size="small"
                    valueLabelDisplay="auto"
                  />
                </Box>

              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Footer Section */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon fontSize="small" />
                <Typography fontWeight={600}>Footer (Подвал)</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.footer?.enabled !== false}
                      onChange={(e) => updateSettings('footer.enabled', e.target.checked)}
                    />
                  }
                  label="Показывать футер"
                />

                {settings.footer?.enabled !== false && (
                  <>
                    <Divider />
                    
                    <Typography variant="subtitle2" fontWeight={600}>Цвета</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        label="Фон"
                        type="color"
                        value={getPickerColor(settings.footer?.backgroundColor, '#1f2937')}
                        onChange={(e) => updateSettings('footer.backgroundColor', e.target.value)}
                        size="small"
                        sx={{ width: 90 }}
                      />
                      <TextField
                        fullWidth
                        label="HTML цвет"
                        value={settings.footer?.backgroundColor || '#1f2937'}
                        onChange={(e) => updateSettings('footer.backgroundColor', e.target.value)}
                        size="small"
                        placeholder="#1f2937"
                      />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        label="Текст"
                        type="color"
                        value={getPickerColor(settings.footer?.textColor, '#ffffff')}
                        onChange={(e) => updateSettings('footer.textColor', e.target.value)}
                        size="small"
                        sx={{ width: 90 }}
                      />
                      <TextField
                        fullWidth
                        label="HTML цвет"
                        value={settings.footer?.textColor || '#ffffff'}
                        onChange={(e) => updateSettings('footer.textColor', e.target.value)}
                        size="small"
                        placeholder="#ffffff"
                      />
                    </Box>

                    <Divider />

                    <Typography variant="subtitle2" fontWeight={600}>Секция 1</Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.footer?.section1Enabled !== false}
                          onChange={(e) => updateSettings('footer.section1Enabled', e.target.checked)}
                        />
                      }
                      label="Показывать секцию 1"
                    />
                    <TextField
                      fullWidth
                      label="Заголовок"
                      value={settings.footer?.section1Title || 'О нас'}
                      onChange={(e) => updateSettings('footer.section1Title', e.target.value)}
                      size="small"
                    />
                    <TextField
                      fullWidth
                      label="HTML контент"
                      value={settings.footer?.section1Html || '<p>Качественные товары по доступным ценам. Работаем для вас каждый день.</p>'}
                      onChange={(e) => updateSettings('footer.section1Html', e.target.value)}
                      size="small"
                      multiline
                      rows={4}
                    />

                    <Divider />

                    <Typography variant="subtitle2" fontWeight={600}>Секция 2 (Контакты)</Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.footer?.section2Enabled !== false}
                          onChange={(e) => updateSettings('footer.section2Enabled', e.target.checked)}
                        />
                      }
                      label="Показывать секцию 2"
                    />
                    <TextField
                      fullWidth
                      label="Заголовок"
                      value={settings.footer?.section2Title || 'Контакты'}
                      onChange={(e) => updateSettings('footer.section2Title', e.target.value)}
                      size="small"
                    />
                    <TextField
                      fullWidth
                      label="HTML контент"
                      value={settings.footer?.section2Html || '<a href="#">Telegram</a><a href="#">Email</a>'}
                      onChange={(e) => updateSettings('footer.section2Html', e.target.value)}
                      size="small"
                      multiline
                      rows={4}
                    />

                    <Divider />

                    <Typography variant="subtitle2" fontWeight={600}>Секция 3</Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.footer?.section3Enabled !== false}
                          onChange={(e) => updateSettings('footer.section3Enabled', e.target.checked)}
                        />
                      }
                      label="Показывать секцию 3"
                    />
                    <TextField
                      fullWidth
                      label="Заголовок"
                      value={settings.footer?.section3Title || 'Информация'}
                      onChange={(e) => updateSettings('footer.section3Title', e.target.value)}
                      size="small"
                    />
                    <TextField
                      fullWidth
                      label="HTML контент"
                      value={settings.footer?.section3Html || ''}
                      onChange={(e) => updateSettings('footer.section3Html', e.target.value)}
                      size="small"
                      multiline
                      rows={4}
                    />

                    <Divider />

                    <Typography variant="subtitle2" fontWeight={600}>Copyright</Typography>
                    <TextField
                      fullWidth
                      label="Текст copyright"
                      value={settings.footer?.copyright || '© 2024 Все права защищены'}
                      onChange={(e) => updateSettings('footer.copyright', e.target.value)}
                      size="small"
                    />
                  </>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Drawer>

      {/* Main Content - Preview */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: '64px',
          bgcolor: '#e5e7eb',
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start'
        }}
      >
        {primaryDomain ? (
          <Box 
            sx={{ 
              width: getViewportWidth(),
              height: viewportMode === 'desktop' ? '100%' : 'auto',
              minHeight: viewportMode !== 'desktop' ? '667px' : 'auto',
              bgcolor: 'white',
              boxShadow: viewportMode !== 'desktop' ? '0 10px 40px rgba(0,0,0,0.2)' : 'none',
              borderRadius: viewportMode !== 'desktop' ? 2 : 0,
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              mx: 'auto'
            }}
          >
            <SitePreview
              settings={previewSettings}
              previewKey={previewKey}
              style={{
                height: viewportMode === 'desktop' ? '100%' : '667px',
                display: 'block'
              }}
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
            <Typography variant="h6" color="text.secondary">
              Добавьте основной домен для предпросмотра
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default VisualEditor;
